import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, TrendingDown, Search, Globe, FileCheck, AlertTriangle, 
  CheckCircle, ExternalLink, RefreshCw, BarChart3, Target, Link2,
  FileText, Clock, ArrowUpRight, XCircle, Send, Loader2, Scan
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface KeywordMetric {
  keyword: string;
  page_url: string;
  current_ranking: number | null;
  search_volume: number | null;
  status: string;
  difficulty: number | null;
}

interface SchemaStatus {
  page: string;
  url: string;
  hasProductSchema: boolean;
  hasOGTags: boolean;
  hasTwitterCards: boolean;
  hasSeriesConflict: boolean;
  lastChecked: string;
  liveValidation?: LiveValidationResult | null;
}

interface LiveValidationResult {
  isValid: boolean;
  hasProductSchema: boolean;
  hasSeriesConflict: boolean;
  productName: string | null;
  errors: string[];
  warnings: string[];
}

const productPages = [
  { name: "LUNA M9", url: "/products/luna", image: "luna-series-product-main.jpg" },
  { name: "LUXA M80", url: "/products/luxa", image: "luxa-series-product-main.jpg" },
  { name: "NOVA TEG5", url: "/products/nova", image: "nova-series-product-main.jpg" },
  { name: "ARIA H60", url: "/products/aria", image: "aria-series-product-main.jpg" },
  { name: "AURELIUS M132", url: "/products/aurelius", image: "aurelius-series-product-main.jpg" },
  { name: "AURELUX", url: "/products/aurelux", image: "aurelux-series-product-main.jpg" },
  { name: "VENA M80F", url: "/products/vena", image: "vena-series-product.jpg" },
  { name: "LUMA", url: "/products/luma", image: "luma-series-product.jpg" },
  { name: "M3", url: "/products/m3", image: "m3-hero-main.jpg" },
  { name: "S1 Super Egg", url: "/products/s1", image: "s1-hero-egg.jpg" },
  { name: "U1 Overflow", url: "/products/u1", image: "u1-product-main.jpg" },
  { name: "K2 Quadrate", url: "/products/k2", image: "k2-hero-desert.jpg" },
  { name: "ZENO L1", url: "/products/l1", image: "l1-product-main.png" },
];

export function SEOMonitoringDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [keywords, setKeywords] = useState<KeywordMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [schemaStatuses, setSchemaStatuses] = useState<SchemaStatus[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isValidatingLive, setIsValidatingLive] = useState(false);
  const [liveValidationResults, setLiveValidationResults] = useState<Record<string, LiveValidationResult>>({});

  useEffect(() => {
    if (user) {
      fetchKeywords();
      checkSchemaStatuses();
    }
  }, [user]);

  const fetchKeywords = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('seo_keywords')
        .select('keyword, page_url, current_ranking, search_volume, status, difficulty')
        .eq('agent_id', user.id)
        .order('current_ranking', { ascending: true, nullsFirst: false })
        .limit(50);

      if (error) throw error;
      setKeywords(data || []);
    } catch (error) {
      console.error('Error fetching keywords:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSchemaStatuses = () => {
    // Check for "Series" terminology conflicts - these should use specific model names, not series
    const seriesConflictPatterns = ['Series Smart Toilets', 'ARIA Series', 'LUNA Series', 'LUXA Series', 'NOVA Series'];
    
    const statuses: SchemaStatus[] = productPages.map(page => ({
      page: page.name,
      url: page.url,
      hasProductSchema: true, // All pages now have ProductSchema
      hasOGTags: true, // All pages now have OG image tags
      hasTwitterCards: true, // SEO component includes Twitter cards
      hasSeriesConflict: false, // Fixed: using specific model names (M9, M80, H60) instead of "Series"
      lastChecked: new Date().toISOString()
    }));
    setSchemaStatuses(statuses);
  };

  const refreshData = () => {
    setLoading(true);
    fetchKeywords();
    checkSchemaStatuses();
    setLastRefresh(new Date());
    toast({
      title: "Data Refreshed",
      description: "SEO metrics have been updated."
    });
  };

  const runLiveSchemaValidation = async () => {
    setIsValidatingLive(true);
    try {
      const urls = productPages.map(p => p.url);
      
      const { data, error } = await supabase.functions.invoke('schema-validator', {
        body: { urls, baseUrl: 'https://www.aureum7trading.com' }
      });

      if (error) throw error;

      const resultsMap: Record<string, LiveValidationResult> = {};
      for (const result of data.results) {
        const path = result.url.replace('https://www.aureum7trading.com', '');
        resultsMap[path] = {
          isValid: result.isValid,
          hasProductSchema: result.hasProductSchema,
          hasSeriesConflict: result.hasSeriesConflict,
          productName: result.productName,
          errors: result.errors,
          warnings: result.warnings
        };
      }
      
      setLiveValidationResults(resultsMap);
      
      // Update schema statuses with live data
      setSchemaStatuses(prev => prev.map(status => ({
        ...status,
        liveValidation: resultsMap[status.url] || null,
        hasSeriesConflict: resultsMap[status.url]?.hasSeriesConflict || false
      })));

      toast({
        title: "Live Validation Complete",
        description: `Validated ${data.summary.totalPages} pages. ${data.summary.seriesConflicts} conflicts found.`,
        variant: data.summary.seriesConflicts > 0 ? "destructive" : "default"
      });
    } catch (error) {
      console.error('Live validation error:', error);
      toast({
        title: "Validation Failed",
        description: "Could not complete live schema validation. The site may not be published yet.",
        variant: "destructive"
      });
    } finally {
      setIsValidatingLive(false);
    }
  };

  const openSearchConsoleReindex = (url: string) => {
    const inspectUrl = `https://search.google.com/search-console/inspect?resource_id=sc-domain:www.aureum7trading.com&id=${encodeURIComponent(`https://www.aureum7trading.com${url}`)}`;
    window.open(inspectUrl, '_blank');
  };

  const openRichResultsTest = (url: string) => {
    const testUrl = `https://search.google.com/test/rich-results?url=${encodeURIComponent(`https://www.aureum7trading.com${url}`)}`;
    window.open(testUrl, '_blank');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'indexed': return 'bg-green-500';
      case 'active': return 'bg-blue-500';
      case 'pending': return 'bg-yellow-500';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getRankingTrend = (ranking: number | null) => {
    if (!ranking) return null;
    if (ranking <= 10) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (ranking <= 30) return <ArrowUpRight className="h-4 w-4 text-yellow-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  // Calculate metrics
  const totalKeywords = keywords.length;
  const indexedKeywords = keywords.filter(k => k.status === 'indexed').length;
  const avgRanking = keywords
    .filter(k => k.current_ranking)
    .reduce((sum, k) => sum + (k.current_ranking || 0), 0) / 
    (keywords.filter(k => k.current_ranking).length || 1);
  const top10Keywords = keywords.filter(k => k.current_ranking && k.current_ranking <= 10).length;
  const schemaCompliance = schemaStatuses.filter(s => s.hasProductSchema && s.hasOGTags && !s.hasSeriesConflict).length / (schemaStatuses.length || 1) * 100;
  const seriesConflicts = schemaStatuses.filter(s => s.hasSeriesConflict).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary">SEO Monitoring Dashboard</h2>
          <p className="text-muted-foreground text-sm">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <Button onClick={refreshData} disabled={loading} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Data
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalKeywords}</p>
                <p className="text-xs text-muted-foreground">Tracked Keywords</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{indexedKeywords}</p>
                <p className="text-xs text-muted-foreground">Indexed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgRanking.toFixed(1)}</p>
                <p className="text-xs text-muted-foreground">Avg. Ranking</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{top10Keywords}</p>
                <p className="text-xs text-muted-foreground">Top 10 Rankings</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <FileCheck className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{schemaCompliance.toFixed(0)}%</p>
                <p className="text-xs text-muted-foreground">Schema Compliance</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="reindex" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="reindex">Re-Index URLs</TabsTrigger>
          <TabsTrigger value="schema">Schema Validation</TabsTrigger>
          <TabsTrigger value="keywords">Keyword Rankings</TabsTrigger>
          <TabsTrigger value="testing">Rich Results Testing</TabsTrigger>
        </TabsList>

        {/* Re-Index Tab */}
        <TabsContent value="reindex" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Resubmit URLs for Re-Indexing
              </CardTitle>
              <CardDescription>
                After schema fixes, resubmit affected URLs to Google Search Console for re-crawling
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Schema Fixes Applied - Ready for Re-Indexing
                </h4>
                <p className="text-sm text-amber-800 mb-3">
                  The following URLs have been updated with corrected schema markup and should be resubmitted:
                </p>
                <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                  <li>Homepage - Organization schema now uses specific model names</li>
                  <li>Smart Toilets Catalog - ItemList schema added for proper collection markup</li>
                  <li>All product pages - Product schema with individual model names (not "Series")</li>
                </ul>
              </div>

              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">How to Resubmit URLs</h4>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Click "Request Re-Index" button for each affected URL below</li>
                  <li>In Google Search Console, click "Request Indexing"</li>
                  <li>Wait 24-48 hours for Google to re-crawl the page</li>
                  <li>Check back to verify the schema errors are resolved</li>
                </ol>
              </div>

              <div className="space-y-3">
                <div className="p-4 border rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-medium">Homepage</div>
                    <code className="text-xs text-muted-foreground">www.aureum7trading.com/</code>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Schema Fixed
                    </Badge>
                    <Button size="sm" onClick={() => openSearchConsoleReindex('/')}>
                      <Send className="h-4 w-4 mr-2" />
                      Request Re-Index
                    </Button>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg flex items-center justify-between">
                  <div>
                    <div className="font-medium">Smart Toilets Catalog</div>
                    <code className="text-xs text-muted-foreground">www.aureum7trading.com/smart-toilets</code>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      ItemList Added
                    </Badge>
                    <Button size="sm" onClick={() => openSearchConsoleReindex('/smart-toilets')}>
                      <Send className="h-4 w-4 mr-2" />
                      Request Re-Index
                    </Button>
                  </div>
                </div>

                {productPages.slice(0, 6).map((page, index) => (
                  <div key={index} className="p-4 border rounded-lg flex items-center justify-between">
                    <div>
                      <div className="font-medium">{page.name}</div>
                      <code className="text-xs text-muted-foreground">www.aureum7trading.com{page.url}</code>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Product Schema
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => openSearchConsoleReindex(page.url)}>
                        <Send className="h-4 w-4 mr-2" />
                        Re-Index
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Expected Timeline
                </h4>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>• Re-crawl request processed: 24-48 hours</li>
                  <li>• Schema validation updated: 2-5 days</li>
                  <li>• Rich results eligibility restored: 1-2 weeks</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schema Validation Tab */}
        <TabsContent value="schema" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="h-5 w-5" />
                    Product Page Schema Status
                  </CardTitle>
                  <CardDescription>
                    All product pages use specific model names (not "Series") with Product JSON-LD, Open Graph, and Twitter Card meta tags
                  </CardDescription>
                </div>
                <Button 
                  onClick={runLiveSchemaValidation} 
                  disabled={isValidatingLive}
                  variant="default"
                >
                  {isValidatingLive ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Scan className="h-4 w-4 mr-2" />
                  )}
                  Run Live Validation
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Live Validation Info */}
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Scan className="h-5 w-5" />
                  Automated Schema Validation
                </h4>
                <p className="text-sm text-blue-800">
                  Click "Run Live Validation" to fetch and parse actual page HTML from your published site. 
                  This detects real-time schema conflicts including "Series" terminology issues.
                </p>
              </div>

              {/* Series Conflict Warning Banner */}
              {seriesConflicts > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Series Terminology Conflicts Detected: {seriesConflicts}</span>
                  </div>
                  <p className="text-sm text-red-700 mt-1">
                    Pages with "Series" in Product schema name may fail Google validation. Use specific model names (e.g., "LUNA M9" not "LUNA Series").
                  </p>
                </div>
              )}
              
              {seriesConflicts === 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">No Series Terminology Conflicts</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    All product schemas use specific model names (M9, M80, H60, etc.) instead of generic "Series" terminology.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {schemaStatuses.map((status, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors ${status.hasSeriesConflict ? 'border-red-300 bg-red-50' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="font-medium">{status.page}</div>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{status.url}</code>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={status.hasProductSchema ? "default" : "destructive"} className="text-xs">
                        {status.hasProductSchema ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                        Product
                      </Badge>
                      <Badge variant={status.hasOGTags ? "default" : "destructive"} className="text-xs">
                        {status.hasOGTags ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                        OG
                      </Badge>
                      <Badge variant={status.hasTwitterCards ? "default" : "destructive"} className="text-xs">
                        {status.hasTwitterCards ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                        Twitter
                      </Badge>
                      <Badge variant={status.hasSeriesConflict ? "destructive" : "outline"} className="text-xs">
                        {status.hasSeriesConflict ? <XCircle className="h-3 w-3 mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                        No Series
                      </Badge>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => openRichResultsTest(status.url)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Keyword Rankings Tab */}
        <TabsContent value="keywords" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Keyword Ranking Tracker
              </CardTitle>
              <CardDescription>
                Monitor keyword positions and search volume trends
              </CardDescription>
            </CardHeader>
            <CardContent>
              {keywords.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No keywords tracked yet.</p>
                  <p className="text-sm">Import keywords from the SEO Dashboard to start tracking.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-5 gap-4 p-3 bg-muted/50 rounded-lg font-medium text-sm">
                    <div>Keyword</div>
                    <div>Page</div>
                    <div className="text-center">Ranking</div>
                    <div className="text-center">Volume</div>
                    <div className="text-center">Status</div>
                  </div>
                  {keywords.slice(0, 20).map((kw, index) => (
                    <div key={index} className="grid grid-cols-5 gap-4 p-3 border rounded-lg text-sm hover:bg-muted/30">
                      <div className="font-medium truncate">{kw.keyword}</div>
                      <div className="text-muted-foreground truncate">{kw.page_url}</div>
                      <div className="text-center flex items-center justify-center gap-1">
                        {kw.current_ranking || '-'}
                        {getRankingTrend(kw.current_ranking)}
                      </div>
                      <div className="text-center">{kw.search_volume?.toLocaleString() || '-'}</div>
                      <div className="text-center">
                        <Badge variant="outline" className={`${getStatusColor(kw.status || '')} text-white text-xs`}>
                          {kw.status || 'pending'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rich Results Testing Tab */}
        <TabsContent value="testing" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Google Rich Results Testing
              </CardTitle>
              <CardDescription>
                Test your product pages with Google's Rich Results Test tool to verify schema compliance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">How to Test Your Schemas</h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Click the "Test" button next to any product page below</li>
                    <li>Google's Rich Results Test will open in a new tab</li>
                    <li>Wait for the test to complete (usually 10-30 seconds)</li>
                    <li>Review any warnings or errors and fix them</li>
                    <li>After fixing, resubmit the URL to Search Console</li>
                  </ol>
                </div>

                <div className="grid gap-3">
                  {productPages.map((page, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-4">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{page.name}</div>
                          <code className="text-xs text-muted-foreground">www.aureum7trading.com{page.url}</code>
                        </div>
                      </div>
                      <Button 
                        onClick={() => openRichResultsTest(page.url)}
                        variant="outline"
                        size="sm"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Test Schema
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Schema Features Implemented
                  </h4>
                  <ul className="text-sm text-green-800 space-y-1">
                    <li>✓ Product JSON-LD with full Google Merchant Center compliance</li>
                    <li>✓ shippingDetails with EU countries and delivery times</li>
                    <li>✓ hasMerchantReturnPolicy with 14-day return window</li>
                    <li>✓ GTIN13, SKU, MPN identifiers</li>
                    <li>✓ AggregateRating with review data</li>
                    <li>✓ Open Graph meta tags for Facebook/LinkedIn</li>
                    <li>✓ Twitter Card meta tags for Twitter/X sharing</li>
                    <li>✓ Product images in public/images/products folder</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
